import {
    Accordion,
    AccordionActions,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    CircularProgress,
    IconButton,
    Snackbar,
    Typography
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useRouteLoaderData, NavLink, Outlet} from "react-router-dom";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import api from "../../../../hooks/useApi";
import EditIcon from '@mui/icons-material/Edit';
import PageTitle from "../../../../components/PageTitle";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {useState} from "react";

export default function ObjectChildren() {
    const routeLoader = useRouteLoaderData('obj_layout').hotelObj;
    const layoutLoader = useRouteLoaderData('obj_layout');
    const hotelObj = layoutLoader.hotelObj;
    const childrenObject = routeLoader.children_obj;
    const [calendarLinks, setCalendarLinks] = useState({});
    const [calendarLoading, setCalendarLoading] = useState({});
    const [calendarErrors, setCalendarErrors] = useState({});
    const [calendarSyncLoading, setCalendarSyncLoading] = useState({});
    const [copyMessage, setCopyMessage] = useState('');

    const handleDelete = async (childId) => {
        try {
            const csrfToken = document.cookie
                .split('; ')
                .find((row) => row.startsWith('csrftoken'))
                ?.split('=')[1]; // Получение CSRF токена из куки

            const response = await fetch(`/api/HotelObjChildren/${childId}/`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken, // Добавляем CSRF токен, если он требуется
                },
            });

            if (response.statusText === 'No Content') {
                alert('Підоб\'єкт успішно видалено');
                window.location.href = `/object/${hotelObj.id}/children`; // Перенаправление после удаления, можно изменить на нужный маршрут
            } else {
                alert('Не вдалося видалити підоб\'єкт');
            }
        } catch (error) {
            console.error('Помилка видалення:', error);
            alert('Виникла помилка при спробі видалити підоб\'єкт');
        }
    };

    const handleAddChild = async () => {
        // Открываем новое окно
        const newWindow = window.open('', '_blank');

        try {
            const id = (await api.get('/api/CreateObjForms/HotelObjChildrenDraft/')).data.id;
            const url = `/no-nav/new-object/${hotelObj.id}/child/draft/${id}/?extraParam=HotelObj`;
            newWindow.location.href = url; // Перенаправляем новое окно на нужный URL
            //console.log('new window updated');
        } catch (e) {
            alert("Помилка відкриття нового вікна: ", e);
            newWindow.close();
        }
    };

    const handleEdit = async (id) => {
        const newWindow = window.open('', '_blank');

        try {
            const url = `/no-nav/new-object/${hotelObj.id}/child/draft/${id}/?extraParam=HotelChildEdit`;
            newWindow.location.href = url; // Перенаправляем новое окно на нужный URL
            //console.log('new window updated');
        } catch (e) {
            alert("Помилка відкриття нового вікна: ", e);
            newWindow.close();
        }
    };

    const handleGetCalendarLink = async (childId) => {
        setCalendarLoading((prev) => ({...prev, [childId]: true}));
        setCalendarErrors((prev) => ({...prev, [childId]: ''}));

        try {
            const {data} = await api.post(`/api/calendar/child/${childId}/link/`);
            setCalendarLinks((prev) => ({...prev, [childId]: data.calendar_url}));
        } catch (error) {
            const message = error?.response?.data?.detail || 'Не вдалося отримати посилання на календар';
            setCalendarErrors((prev) => ({...prev, [childId]: message}));
        } finally {
            setCalendarLoading((prev) => ({...prev, [childId]: false}));
        }
    };

    const handleSyncCalendar = async (childId) => {
        setCalendarSyncLoading((prev) => ({...prev, [childId]: true}));
        setCalendarErrors((prev) => ({...prev, [childId]: ''}));

        try {
            const {data} = await api.post(`/api/calendar/child/${childId}/sync/`);
            setCopyMessage(
                `Календар оновлено: created ${data.created ?? 0}, updated ${data.updated ?? 0}, deleted ${data.deleted ?? 0}`
            );
        } catch (error) {
            const message = error?.response?.data?.detail || error?.response?.data?.error || 'Не вдалося оновити календар';
            setCalendarErrors((prev) => ({...prev, [childId]: message}));
        } finally {
            setCalendarSyncLoading((prev) => ({...prev, [childId]: false}));
        }
    };

    const handleCopyCalendarLink = async (childId) => {
        const url = calendarLinks[childId];
        if (!url) {
            return;
        }
        try {
            await navigator.clipboard.writeText(url);
            setCopyMessage('Посилання скопійовано');
        } catch (e) {
            setCopyMessage('Не вдалося скопіювати посилання');
        }
    };

    return (
        <>
            <PageTitle title={`Підоб'єкти: ${hotelObj?.name || hotelObj?.id || "ID"}`}/>
            {/* Десктопная версия */}
            <Box sx={{display: {xs: 'none', md: 'block'}}}>
                <Box>
                    {childrenObject.length === 0 && (
                        <Box>
                            Дочірні об'єкти відсутні {':('}
                        </Box>
                    )}
                    {childrenObject.map((el, index) => (
                        <Accordion key={index}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                                {el.name}{' '}
                                <span style={{fontSize: '0.8em', marginLeft: '4px'}}>
                                    ({el.service_name})
                                </span><br/>
                                <span style={{fontSize: '0.8em', marginLeft: '4px'}}>
                                    {`Загальна: ${el.guest_number}, дорослих: ${el.guest_number_adults}, дітей: ${el.guest_number_children}`}
                                </span>
                            </AccordionSummary>
                            <Divider orientation="horizontal"/>
                            <AccordionDetails>
                                <DescriptionBlock
                                    title="Назва"
                                    field="name"
                                    id={el.id}
                                    text={`${el.name} (${el.service_name})`}
                                />
                                <DescriptionBlock
                                    title="Опис"
                                    field="descriptions"
                                    id={el.id}
                                    text={el.descriptions}
                                />
                                <DescriptionBlock
                                    title="Класифікація"
                                    field="classification"
                                    id={el.id}
                                    text={el.classification.name}
                                />
                                <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2}}>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => handleGetCalendarLink(el.id)}
                                        disabled={Boolean(calendarLoading[el.id])}
                                    >
                                        {calendarLoading[el.id] ?
                                            <CircularProgress size={18}/> : 'Отримати посилання на календар'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="success"
                                        onClick={() => handleSyncCalendar(el.id)}
                                        disabled={Boolean(calendarSyncLoading[el.id])}
                                    >
                                        {calendarSyncLoading[el.id] ?
                                            <CircularProgress size={18}/> : 'Оновити календар'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<DeleteIcon/>}
                                        onClick={() => handleDelete(el.id)}
                                    >
                                        Видалити підоб'єкт
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<EditIcon/>}
                                        onClick={() => handleEdit(el.id)}
                                    >
                                        Редагувати підоб'єкт
                                    </Button>
                                </Box>
                                {calendarLinks[el.id] && (
                                    <Box sx={{mt: 1, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1}}>
                                        <Typography variant="body2" sx={{wordBreak: 'break-all'}}>
                                            {calendarLinks[el.id]}
                                        </Typography>
                                        <Button
                                            size="small"
                                            startIcon={<ContentCopyIcon/>}
                                            onClick={() => handleCopyCalendarLink(el.id)}
                                            sx={{mt: 1}}
                                        >
                                            Скопіювати
                                        </Button>
                                    </Box>
                                )}
                                {calendarErrors[el.id] && (
                                    <Typography color="error" variant="body2" sx={{mt: 1}}>
                                        {calendarErrors[el.id]}
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    <Box>
                        <Button variant="contained" onClick={handleAddChild}>
                            Додати дочірний об'єкт
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Мобильная версия */}
            <Box sx={{display: {xs: 'block', md: 'none'}}}>
                <Box>
                    {childrenObject.length === 0 && (
                        <Box sx={{textAlign: 'center'}}>
                            Дочірні об'єкти відсутні {':('}
                        </Box>
                    )}
                    {childrenObject.map((el, index) => (
                        <Box
                            key={index}
                            sx={{
                                mb: 2,
                                p: 2,
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                backgroundColor: '#f9f9f9',
                            }}
                        >
                            <Typography variant="h6" sx={{mb: 1}}>
                                {el.name} ({el.service_name})
                            </Typography>
                            <Typography>
                                <strong>Опис:</strong>
                            </Typography>
                            <Box
                                dangerouslySetInnerHTML={{__html: el.descriptions}}
                                sx={{mb: 1}}
                            />
                            <Typography>
                                <strong>Класифікація:</strong> {el.classification.name}
                            </Typography>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 2}}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    onClick={() => handleGetCalendarLink(el.id)}
                                    disabled={Boolean(calendarLoading[el.id])}
                                >
                                    {calendarLoading[el.id] ? <CircularProgress size={16}/> : 'Календар'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="success"
                                    size="small"
                                    onClick={() => handleSyncCalendar(el.id)}
                                    disabled={Boolean(calendarSyncLoading[el.id])}
                                >
                                    {calendarSyncLoading[el.id] ? <CircularProgress size={16}/> : 'Оновити календар'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={() => handleDelete(el.id)}
                                >
                                    Видалити
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    onClick={() => handleEdit(el.id)}
                                >
                                    Редагувати
                                </Button>
                            </Box>
                            {calendarLinks[el.id] && (
                                <Box sx={{mt: 1}}>
                                    <Typography variant="caption" sx={{wordBreak: 'break-all'}}>
                                        {calendarLinks[el.id]}
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<ContentCopyIcon/>}
                                        onClick={() => handleCopyCalendarLink(el.id)}
                                        sx={{mt: 0.5}}
                                    >
                                        Скопіювати
                                    </Button>
                                </Box>
                            )}
                            {calendarErrors[el.id] && (
                                <Typography color="error" variant="caption" sx={{display: 'block', mt: 1}}>
                                    {calendarErrors[el.id]}
                                </Typography>
                            )}
                        </Box>
                    ))}
                    <Box sx={{textAlign: 'center', mt: 3}}>
                        <Button variant="contained" onClick={handleAddChild}>
                            Додати
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Outlet/>
            <Snackbar
                open={Boolean(copyMessage)}
                autoHideDuration={2500}
                onClose={() => setCopyMessage('')}
                message={copyMessage}
            />
        </>
    )
}
const DescriptionBlock = ({
                              title, field, text, id
                          }) => {
    return (
        <Box sx={{my: 2}}>
            <Box className="flex stickSides">
                <Typography variant="h5">{title}</Typography>
                <IconButton component={NavLink} to={`edit/${id}/${field}`}>
                    <EditOutlinedIcon/>
                </IconButton>
            </Box>
            {field === 'descriptions' ? (
                <Box dangerouslySetInnerHTML={{__html: text}}/>
            ) : (
                <Box>
                    <Typography>{text}</Typography>
                </Box>
            )}
        </Box>
    );
}
